import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Send,
  Search,
  User,
  MessageSquare,
  Loader2,
  Bot,
  ChevronLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  useMessages,
  useConversation,
  useSendMessage,
  useMe,
  useMarkAsRead
} from "@/hooks/use-queries";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { resolveAvatarUrl } from "@/lib/utils";

export default function Messages() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedUserId = searchParams.get("user");
  const selectedAgentId = searchParams.get("agent");

  const { data: me } = useMe();
  const { data: messages, isLoading: loadingMessages } = useMessages();
  const { data: conversation, isLoading: loadingConversation } = useConversation(selectedUserId || "");
  const sendMessageMutation = useSendMessage();
  const markAsReadMutation = useMarkAsRead();

  const [newMessage, setNewMessage] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showMentionSuggestion, setShowMentionSuggestion] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Group messages by conversation partner
  const conversations = messages?.reduce((acc: any, msg: any) => {
    const partner = msg.senderId === me?.id ? msg.receiver : msg.sender;
    if (!partner) return acc;

    if (!acc[partner.id]) {
      acc[partner.id] = {
        partner,
        lastMessage: msg,
        unreadCount: (msg.receiverId === me?.id && !msg.readAt) ? 1 : 0
      };
    } else {
      if (msg.receiverId === me?.id && !msg.readAt) {
        acc[partner.id].unreadCount++;
      }
      if (new Date(msg.createdAt) > new Date(acc[partner.id].lastMessage.createdAt)) {
        acc[partner.id].lastMessage = msg;
      }
    }
    return acc;
  }, {}) || {};

  const sortedConversations = Object.values(conversations).sort((a: any, b: any) =>
    new Date(b.lastMessage.createdAt).getTime() - new Date(a.lastMessage.createdAt).getTime()
  );

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation]);

  // Mark as read
  useEffect(() => {
    if (selectedUserId && conversation) {
      conversation.forEach((msg: any) => {
        if (msg.receiverId === me?.id && !msg.readAt) {
          markAsReadMutation.mutate(msg.id);
        }
      });
    }
  }, [selectedUserId, conversation]);

  // Detect @ mention trigger
  useEffect(() => {
    if (!selectedAgentId) {
      setShowMentionSuggestion(false);
      return;
    }

    const textBeforeCursor = newMessage.slice(0, cursorPosition);
    const lastAtPosition = textBeforeCursor.lastIndexOf('@');

    if (lastAtPosition !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtPosition + 1);
      // Show suggestion if @ is at word boundary and text after is empty or matches 'agent' partially
      if (textAfterAt.length === 0 || 'agent'.startsWith(textAfterAt.toLowerCase())) {
        setShowMentionSuggestion(true);
        return;
      }
    }

    setShowMentionSuggestion(false);
  }, [newMessage, cursorPosition, selectedAgentId]);

  const renderMessageContent = (content: string) => {
    const parts = content.split(/(@agent\b)/gi);
    return parts.map((part, index) => {
      if (part.toLowerCase() === '@agent') {
        return (
          <span key={index} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/25 border border-primary/30 font-semibold">
            <Bot className="h-3 w-3" />
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const insertMention = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = newMessage;

    // Find @ before cursor
    const textBeforeCursor = text.slice(0, start);
    const lastAtPosition = textBeforeCursor.lastIndexOf('@');

    let newText = '';
    let newCursorPos = start;

    if (lastAtPosition !== -1) {
      // Replace from @ to cursor with @agent
      newText = text.slice(0, lastAtPosition) + '@agent ' + text.slice(end);
      newCursorPos = lastAtPosition + 7; // '@agent ' length
    } else {
      // Insert @agent at cursor
      newText = text.slice(0, start) + '@agent ' + text.slice(end);
      newCursorPos = start + 7;
    }

    setNewMessage(newText);
    setShowMentionSuggestion(false);

    // Set cursor position after state update
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        setCursorPosition(newCursorPos);
      }
    }, 0);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    setCursorPosition(e.target.selectionStart);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionSuggestion && e.key === 'Tab') {
      e.preventDefault();
      insertMention();
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (!selectedUserId || !newMessage.trim()) return;

    try {
      await sendMessageMutation.mutateAsync({
        receiverId: selectedUserId,
        agentId: selectedAgentId || undefined,
        content: newMessage.trim()
      });
      setNewMessage("");
      setShowMentionSuggestion(false);
      setCursorPosition(0);
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const handleSelectUser = (userId: string) => {
    const params: any = { user: userId };
    if (selectedAgentId) params.agent = selectedAgentId;
    setSearchParams(params);
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col md:flex-row gap-0 md:gap-4 p-3 md:p-6 overflow-hidden">
      {/* Sidebar */}
      <Card className={`glass-panel w-full md:w-80 lg:w-96 flex flex-col h-full border-border/50 bg-background/50 backdrop-blur-xl ${selectedUserId ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 md:p-5 border-b border-border/40 space-y-3 shrink-0">
          <h2 className="font-display font-bold text-base md:text-lg flex items-center gap-2 text-foreground">
            <MessageSquare className="h-5 w-5 text-primary" /> Messages
          </h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              className="pl-10 bg-background/60 border-border/50 h-10 text-sm focus-visible:ring-primary/30 focus-visible:border-primary/50 transition-all"
            />
          </div>
        </div>
        <ScrollArea className="flex-1">
          <div className="flex flex-col p-2 gap-1">
            {loadingMessages ? (
              <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary/50" /></div>
            ) : sortedConversations.length === 0 ? (
              <div className="text-center py-16 px-4 text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm">No conversations yet</p>
              </div>
            ) : (
              (sortedConversations as any[]).map(({ partner, lastMessage, unreadCount }) => (
                <button
                  key={partner.id}
                  onClick={() => handleSelectUser(partner.id)}
                  className={`flex items-start gap-3 p-3 rounded-xl text-left transition-all duration-200 ${
                    selectedUserId === partner.id
                      ? "bg-primary/10 border border-primary/30 shadow-sm"
                      : "hover:bg-muted/40 border border-transparent hover:border-border/40"
                  }`}
                >
                  <Avatar className="h-11 w-11 border-2 border-border/40 ring-2 ring-offset-2 ring-offset-background ring-transparent group-hover:ring-primary/20 transition-all">
                    <AvatarImage src={resolveAvatarUrl(partner.avatarUrl)} />
                    <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-semibold">
                      {partner.displayName?.charAt(0) || <User className="h-5 w-5" />}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline mb-0.5">
                      <span className={`text-sm font-semibold truncate ${selectedUserId === partner.id ? "text-primary" : "text-foreground"}`}>
                        {partner.displayName}
                      </span>
                      <span className="text-[9px] text-muted-foreground/70 shrink-0 ml-2 font-mono uppercase">
                        {formatDistanceToNow(new Date(lastMessage.createdAt), { addSuffix: false }).replace('about ', '')}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate leading-relaxed">
                      {lastMessage.senderId === me?.id && <span className="font-medium">You: </span>}
                      {lastMessage.content}
                    </p>
                  </div>
                  {unreadCount > 0 && (
                    <Badge className="h-5 min-w-[1.25rem] px-1.5 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold shadow-sm">
                      {unreadCount}
                    </Badge>
                  )}
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </Card>

      {/* Chat Area */}
      <Card className={`glass-panel flex-1 flex flex-col h-full border-border/50 bg-background/50 backdrop-blur-xl overflow-hidden ${!selectedUserId ? 'hidden md:flex' : 'flex'}`}>
        {selectedUserId ? (
          <>
            {/* Header */}
            <div className="p-3 md:p-4 border-b border-border/40 flex items-center gap-3 shrink-0 bg-gradient-to-r from-background/40 to-background/60 backdrop-blur-md">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-9 w-9 hover:bg-muted/50"
                onClick={() => setSearchParams({})}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              {(() => {
                const partner = (sortedConversations as any[]).find((c: any) => c.partner.id === selectedUserId)?.partner;
                return (
                  <>
                    <Avatar className="h-10 w-10 md:h-11 md:w-11 border-2 border-primary/20 shadow-sm">
                      <AvatarImage src={resolveAvatarUrl(partner?.avatarUrl)} />
                      <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-semibold">
                        {partner?.displayName?.charAt(0) || <User className="h-5 w-5" />}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="font-bold text-sm md:text-base text-foreground truncate">{partner?.displayName || "Unknown User"}</h3>
                        {selectedAgentId && (
                          <Badge variant="outline" className="text-[9px] bg-primary/10 border-primary/30 text-primary font-semibold shrink-0">
                            <Bot className="h-3 w-3 mr-1" />
                            AI Agent
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                        <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-wide">
                          {selectedAgentId ? 'Three-way conversation' : 'Online'}
                        </p>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 md:p-5 space-y-3 md:space-y-4" ref={scrollRef} style={{ scrollBehavior: 'smooth' }}>
              {loadingConversation ? (
                <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary/50" /></div>
              ) : conversation?.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground">
                  <div className="h-20 w-20 bg-primary/5 rounded-full flex items-center justify-center mx-auto mb-4 border border-primary/10">
                     <MessageSquare className="h-10 w-10 text-primary/30" />
                  </div>
                  <p className="text-sm font-medium">Start the conversation!</p>
                  <p className="text-xs mt-1 opacity-70">Send a message to begin chatting</p>
                </div>
              ) : (
                conversation?.map((msg: any, index: number) => {
                  const isMe = msg.senderId === me?.id;
                  const showAvatar = index === 0 || conversation[index - 1]?.senderId !== msg.senderId;

                  return (
                    <div key={msg.id} className={`flex gap-2 ${isMe ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
                      {!isMe && showAvatar && (
                        <Avatar className="h-8 w-8 border border-border/30 shrink-0">
                          <AvatarImage src={resolveAvatarUrl((sortedConversations as any[]).find((c: any) => c.partner.id === selectedUserId)?.partner?.avatarUrl)} />
                          <AvatarFallback className="bg-muted text-xs">
                            <User className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                      )}
                      {!isMe && !showAvatar && <div className="w-8 shrink-0" />}

                      <div className={`max-w-[85%] sm:max-w-[75%] md:max-w-[65%] rounded-2xl px-3 md:px-4 py-2 md:py-2.5 text-sm shadow-sm ${
                        isMe
                          ? "bg-primary text-primary-foreground rounded-tr-md"
                          : "bg-muted/90 text-foreground rounded-tl-md border border-border/30"
                      }`}>
                        <p className="leading-relaxed break-words">{renderMessageContent(msg.content)}</p>
                        <p className={`text-[9px] mt-1 text-right font-mono ${isMe ? "text-primary-foreground/60" : "text-muted-foreground/70"}`}>
                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Input */}
            <div className="p-3 md:p-4 border-t border-border/40 bg-gradient-to-r from-background/40 to-background/60 backdrop-blur-md shrink-0">
              {selectedAgentId && (
                <div className="mb-3 p-2.5 bg-primary/5 border border-primary/10 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Bot className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground mb-0.5">AI Agent Included</p>
                      <p className="text-[10px] text-muted-foreground leading-relaxed">
                        Type <kbd className="px-1.5 py-0.5 bg-muted rounded text-[9px] font-mono border border-border/50">@</kbd> and press <kbd className="px-1.5 py-0.5 bg-muted rounded text-[9px] font-mono border border-border/50">Tab</kbd> to mention the agent directly
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs bg-primary/10 border-primary/20 hover:bg-primary/20 hover:border-primary/30 shrink-0"
                      onClick={insertMention}
                    >
                      <Bot className="h-3 w-3 mr-1" />
                      @agent
                    </Button>
                  </div>
                </div>
              )}

              {showMentionSuggestion && (
                <div className="mb-2 p-2 bg-primary/10 border border-primary/20 rounded-lg animate-in slide-in-from-top-2 duration-200">
                  <button
                    onClick={insertMention}
                    className="w-full text-left flex items-center gap-2 text-sm hover:bg-primary/5 p-2 rounded transition-colors"
                  >
                    <Bot className="h-4 w-4 text-primary" />
                    <span className="font-mono font-semibold text-primary">@agent</span>
                    <span className="text-xs text-muted-foreground ml-auto">Press Tab</span>
                  </button>
                </div>
              )}

              <div className="flex gap-2 items-end">
                <div className="flex-1 relative">
                  <Textarea
                    ref={textareaRef}
                    placeholder={selectedAgentId ? "Type @ to mention agent..." : "Type a message..."}
                    className="min-h-[48px] md:min-h-[52px] max-h-[120px] resize-none bg-background/70 border-border/50 focus-visible:ring-primary/30 focus-visible:border-primary/40 text-sm rounded-xl pr-3"
                    value={newMessage}
                    onChange={handleMessageChange}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => setCursorPosition((e.target as HTMLTextAreaElement).selectionStart)}
                  />
                </div>
                <Button
                  size="icon"
                  className="h-12 w-12 shrink-0 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25 transition-all hover:scale-105 active:scale-95 rounded-xl"
                  onClick={handleSend}
                  disabled={sendMessageMutation.isPending || !newMessage.trim()}
                >
                  {sendMessageMutation.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="h-24 w-24 bg-gradient-to-br from-primary/10 to-primary/5 rounded-full flex items-center justify-center mb-6 border-2 border-primary/10">
              <MessageSquare className="h-12 w-12 text-primary/40" />
            </div>
            <h3 className="text-xl font-display font-bold mb-2 text-foreground">Select a Conversation</h3>
            <p className="max-w-sm text-sm text-muted-foreground leading-relaxed">
              Choose a user from the sidebar to start chatting or check progress on your jobs.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
